import { z } from 'zod';
import { AuditActionSchema, RecordKindSchema } from './actions.js';
import { AuditEntityTypeSchema } from './entities.js';
// Audit-log record shape + the per-kind payload contract (ADR-020 + ADR-058).
//
// A row is SELF-DESCRIBING: `kind` (the shape discriminator, derived from `action`
// at write time) + `entity_label` (the subject's display name, snapshotted at the
// change) + a `previous_value`/`new_value` payload shaped to the kind. The reader
// switches on `kind` and renders from the row alone — it never re-derives what
// changed nor joins to live entity state.
//
// Stored columns (see backend/migrations/000_baseline_schema.sql `audit_logs`):
//   id, project_id, user_id, api_client_id, correlation_id, entity_type,
//   entity_id, entity_label, action, kind, previous_value, new_value,
//   ip_address, user_agent, created_at.
//
// 3-actor model (ADR-017 + ADR-020): at least one of (`user_id`, `api_client_id`)
// carries attribution; system rows have neither. Service-on-behalf-of-user carries
// both — `user_id` is the human-attribution slot in either case.
//
// FK-less by design: `user_id`, `api_client_id`, `entity_id` survive hard-delete of
// any referenced actor or entity (forensic integrity). `project_id` keeps its FK.
//
// **NEVER** put passwords, secrets, tokens, API keys, MFA seeds, or any encrypted
// column in the payload — PR-PROCESS §Cross-cutting (audit / no-secrets) enforces.
// A labelled entity reference stored INSIDE a payload (ADR-058). We snapshot the
// display name at write time so the row renders its historical truth without a live
// join (and survives the referent's hard-delete); the id still drives an optional
// live peek on the read surface. Coded enum values are NOT refs — they are stored
// as the bare code and localized at render.
export const AuditRefSchema = z
    .object({
    id: z.string().max(64),
    label: z.string(),
})
    .strict();
// One edge of a `link` record: the related entity + optional edge attributes
// (e.g. a responsibility level, a membership role, an ordering position).
export const AuditLinkEdgeSchema = z
    .object({
    target: AuditRefSchema,
    attrs: z.record(z.string(), z.unknown()).optional(),
})
    .strict();
// The `link` payload (stored in new_value): edges added and/or removed in one
// logical relationship-change action.
export const AuditLinkPayloadSchema = z
    .object({
    added: z.array(AuditLinkEdgeSchema).optional(),
    removed: z.array(AuditLinkEdgeSchema).optional(),
})
    .strict();
// The `state_change` payload (stored in new_value): the target state + any real
// captured input (e.g. a suspend reason). `from` is optional — captured only when
// the service already holds the prior state (no extra read just for cosmetics).
export const AuditTransitionPayloadSchema = z
    .looseObject({
    to: z.string(),
    from: z.string().optional(),
});
// The stored/read record shape. `id` is the bigint identity PK serialized as a
// string; `correlation_id` is nullable (ADR-020); payload blobs are free-form jsonb
// whose SHAPE is governed per-kind by the write helpers (not a DB constraint).
export const AuditRecordSchema = z
    .object({
    id: z.string(),
    project_id: z.string().max(16).nullable(),
    user_id: z.string().max(16).nullable(),
    api_client_id: z.string().max(16).nullable(),
    correlation_id: z.string().uuid().nullable(),
    entity_type: AuditEntityTypeSchema,
    entity_id: z.string().max(20).nullable(),
    entity_label: z.string().nullable(),
    action: AuditActionSchema,
    kind: RecordKindSchema,
    previous_value: z.record(z.string(), z.unknown()).nullable(),
    new_value: z.record(z.string(), z.unknown()).nullable(),
    ip_address: z.string().nullable(),
    user_agent: z.string().nullable(),
    created_at: z.string(),
})
    .strict();
